/**
 * Initializes and manages Klaviyo reviews functionality.
 * This function sets up formatting for review star counts and observes DOM changes
 * to update the formatting dynamically.
 *
 * @function initKlaviyoReviews
 * @description
 * - Formats the star count text for Klaviyo reviews.
 * - Sets up a MutationObserver to watch for DOM changes and update formatting.
 * - Automatically disconnects the observer when all elements are formatted.
 */
function initKlaviyoReviews() {
  let klaviyoObserver;

  function formatKlaviyoStarsCount() {
    // Explicitly exclude mobile-stars-review and custom-star-rating-label
    const starsCounts = document.querySelectorAll(
      ".kl_reviews__star_rating_widget__label:not(.mobile-stars-review *):not(.custom-star-rating-label), .kl_reviews__summary__stars__count"
    );

    if (starsCounts.length) {
      starsCounts.forEach((starsCount) => {
        // Multiple checks to ensure we don't touch mobile elements
        if (
          starsCount.closest(".mobile-stars-review") ||
          starsCount.classList.contains("custom-star-rating-label") ||
          starsCount.closest(".custom-star-rating-label")
        ) {
          return;
        }

        const originalText = starsCount.textContent.trim();
        const numberPart = originalText.split(" ")[0].replace(/,/g, "");
        const convertToNumber = parseInt(numberPart, 10);

        if (!isNaN(convertToNumber)) {
          const newFormattedNumber = convertToNumber.toLocaleString("en-US");
          const reviewText = convertToNumber === 1 ? "Review" : "Reviews";
          const result = `${newFormattedNumber} ${reviewText}`;

          if (starsCount.textContent !== result) {
            starsCount.textContent = result;
          }
        }
      });
    }
  }

  function observeKlaviyo() {
    klaviyoObserver = new MutationObserver((mutationsList, observer) => {
      formatKlaviyoStarsCount();
    });

    klaviyoObserver.observe(document.body, { childList: true, subtree: true });
  }

  formatKlaviyoStarsCount();
  observeKlaviyo();
}

/**
 * Updates mobile star review text by extracting the number and replacing the element
 * with a styled custom label to prevent Klaviyo's formatting.
 *
 * @function updateMobileStarText
 * @description Transforms "144 Reviews" to "144" in mobile star rating widget
 * @example
 * updateMobileStarText();
 * // Before: <div class="kl_reviews__star_rating_widget__label">144 Reviews</div>
 * // After:  <div class="custom-star-rating-label" data-mobile-review="true">144</div>
 */
function updateMobileStarText() {
  // First, check if we already have a custom label
  const existingCustomLabel = document.querySelector(
    '.custom-star-rating-label[data-mobile-review="true"]'
  );
  if (existingCustomLabel) {
    return; // Exit if we already have our custom label
  }

  const mobileKlaviyoStarElem = document.querySelector(
    ".mobile-stars-review .kl_reviews__star_rating_widget__label"
  );

  if (!mobileKlaviyoStarElem) {
    return;
  }

  // Get the number from the text content
  const currentText = mobileKlaviyoStarElem.textContent.trim();
  const number = currentText.split(" ")[0];

  // Instead of replacing, append the new element
  const newElement = document.createElement("div");
  newElement.className = "custom-star-rating-label";
  newElement.setAttribute("data-mobile-review", "true");
  newElement.textContent = number;

  // Hide the original element instead of replacing it
  mobileKlaviyoStarElem.style.display = "none";

  // Insert after the original element
  mobileKlaviyoStarElem.parentNode.insertBefore(
    newElement,
    mobileKlaviyoStarElem.nextSibling
  );

  // Add styles if they haven't been added yet
  if (!document.querySelector("#mobile-star-styles")) {
    const style = document.createElement("style");
    style.id = "mobile-star-styles";
    style.textContent = `
      .custom-star-rating-label {
        color: var(--kl-reviews-stars-primary-font-color, #767676);
        font-family: var(--kl-reviews-stars-primary-font-font-family, inherit);
        font-weight: var(--kl-reviews-stars-primary-font-font-weight, 400);
        margin-left: 4px;
      }
      .mobile-stars-review .custom-star-rating-label {
        color: var(--kl-reviews-stars-primary-font-color, #767676) !important;
      }
      .mobile-stars-review .kl_reviews__star_rating_widget__label {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Controls the visibility of showcase slider and review carousel on the activity landing page.
 *
 * @function handleActivityLPAdsControl
 * @description
 * This function checks if the current page is an activity landing page and manages the visibility
 * of the showcase slider and review carousel based on URL parameters.
 *
 * @remarks
 * - Checks for the presence of 'page.activity-lp-v2' class on the body element.
 * - Looks for 'show-bs' and 'show-review' URL parameters.
 * - If a parameter is present and set to 'false', the corresponding element is hidden.
 * - The function will not execute if the page is not an activity landing page or if target elements are missing.
 *
 * @example
 * // URL: https://example.com/activity?show-bs=false&show-review=true
 * handleActivityLPAdsControl();
 * // Result: Showcase slider will be hidden, review carousel will be visible
 */
function handleActivityLPAdsControl() {
  const hasActivityLpClass = document.body.classList.contains(
    "page.activity-lp-v2"
  );
  const bsSliderElem = document.querySelector(".section-showcase-slider");
  const reviewSliderElem = document.querySelector(
    ".section-text-card-carousel"
  );

  if (!hasActivityLpClass || !bsSliderElem || !reviewSliderElem) {
    return;
  }

  const currentUrl = new URL(window.location.href);
  const presentBsParam = currentUrl.searchParams.get("show-bs");
  const presentReviewParam = currentUrl.searchParams.get("show-review");

  if (presentBsParam !== null && presentBsParam.toLowerCase() === "false") {
    bsSliderElem.classList.add("hide-this");
  } else {
    bsSliderElem.classList.remove("hide-this");
  }

  if (
    presentReviewParam !== null &&
    presentReviewParam.toLowerCase() === "false"
  ) {
    reviewSliderElem.classList.add("hide-this");
  } else {
    reviewSliderElem.classList.remove("hide-this");
  }
}

/**
 * Handles the sticky header behavior when the menu is opening.
 *
 * This function manages the visibility and positioning of the header element
 * based on the presence of the "menu-opening" class on the body and scroll events.
 * It also handles resize events to adjust the header's top position.
 *
 * @function
 * @name handleMenuOpeningStickyHeader
 *
 * @description
 * - Applies sticky style to the header when the menu is opening
 * - Hides the header when scrolling down while the menu is opening
 * - Shows the header when scrolling to the top while the menu is opening
 * - Adjusts the header's top position on window resize
 *
 * @example
 * // Call the function to initialize the sticky header behavior
 * handleMenuOpeningStickyHeader();
 */
function handleMenuOpeningStickyHeader() {
  const header = document.getElementById("shopify-section-header");
  const body = document.body;
  let isMenuOpening = false;
  let lastScrollTop = 0;
  let resizeTimer;

  function getTopValue() {
    return window.matchMedia("(max-width: 767px)").matches ? "32px" : "36px";
  }

  function applyStickyStyle() {
    header.classList.add("sticky-fixed");
    header.style.setProperty("top", getTopValue());
    header.style.visibility = "visible";
  }

  function resetTopStyleAndHide() {
    header.style.removeProperty("top");
    header.style.visibility = "hidden";
  }

  function handleClassChange(mutations) {
    for (let mutation of mutations) {
      if (mutation.attributeName === "class") {
        isMenuOpening = body.classList.contains("menu-opening");
        if (isMenuOpening) {
          applyStickyStyle();
        } else {
          header.classList.remove("sticky-fixed");
          header.style.removeProperty("top");
          header.style.visibility = "visible";
        }
        break;
      }
    }
  }

  function handleScroll() {
    if (isMenuOpening) {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      if (scrollTop > lastScrollTop) {
        resetTopStyleAndHide();
      } else {
        if (scrollTop > 0) {
          resetTopStyleAndHide();
        } else {
          applyStickyStyle();
        }
      }

      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }
  }

  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (isMenuOpening && header.style.visibility === "visible") {
        header.style.setProperty("top", getTopValue());
      }
    }, 250);
  }

  const observer = new MutationObserver(handleClassChange);
  observer.observe(body, { attributes: true, attributeFilter: ["class"] });

  window.addEventListener("scroll", handleScroll);
  window.addEventListener("resize", handleResize);
}

/**
 * Redirects the user from the "/collections/all" page to the "/collections/shop-all" page.
 *
 * This function checks if the current URL exactly matches the "/collections/all" path.
 * If it does, it redirects the user to the "/collections/shop-all" page on the same domain.
 *
 * @function
 * @name redirectCollectionAll
 * @returns {void}
 *
 * @example
 * // Call the function when the page loads
 * redirectCollectionAll();
 */
function redirectCollectionAll() {
  // Get the current URL
  const currentUrl = window.location.href;

  // Check if the URL exactly matches the target path
  if (currentUrl === window.location.origin + "/collections/all") {
    // Redirect to the desired URL
    window.location.href = window.location.origin + "/collections/polarized";
  }
}

/**
 * Adds click event listeners to product review links and ensures proper navigation.
 *
 * This function selects all elements with the class "klaviyo-star-rating-wrapper",
 * adds click event listeners to each, and handles the navigation manually to
 * bypass potential issues with default link behavior.
 *
 * @function
 * @name handleClickOnProductReviewLink
 * @returns {void}
 *
 * @example
 * // Call the function after the DOM is fully loaded
 * document.addEventListener('DOMContentLoaded', handleClickOnProductReviewLink);
 */
function handleClickOnProductReviewLink() {
  const body = document.body;
  const bodyClasses = body.className.split(" ");

  // Check if the body has a class that starts with "page." or "collection."
  if (
    !bodyClasses.some(
      (className) =>
        className.startsWith("page.") || className.startsWith("collection.")
    )
  ) {
    return;
  }

  const reviewLinks = document.querySelectorAll(".klaviyo-star-rating-wrapper");

  if (!reviewLinks.length) {
    return;
  }

  // If links are found, let's add a more verbose event listener
  reviewLinks.forEach((link, _) => {
    link.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        window.location.href = link.getAttribute("href");
      },
      true
    ); // Use capturing phase
  });

  // Let's also check the parent elements
  reviewLinks.forEach((link, index) => {
    let parent = link.parentElement;
    let depth = 0;
    while (parent && depth < 5) {
      parent = parent.parentElement;
      depth++;
    }
  });
}

/**
 * Handles style changes for Klaviyo review section.
 *
 * @returns void
 */
function handleStyleChangeKlaviyoReview() {
  const klaviyoTitleElem = document.querySelector(
    "#pdp-review-section .kl_reviews__summary__header"
  );
  const klaviyoReviewBtnElem = document.querySelector(
    "#pdp-review-section .kl_reviews__button_bar"
  );

  if (!klaviyoTitleElem || !klaviyoReviewBtnElem) {
    return;
  }

  klaviyoReviewBtnElem.classList.add("klaviyo-review-btn-custom");
  klaviyoTitleElem.before(klaviyoReviewBtnElem);
}

/**
 * Handles Klaviyo review links in modals to navigate to product review sections.
 * Uses event delegation for memory efficiency.
 */
function handleModalReviewLinks() {
  // Single event listener
  const clickHandler = (e) => {
    const reviewLink = e.target.closest(
      ".ombraz-v2pdp__dialog-content .klaviyo-star-rating-wrapper"
    );
    if (!reviewLink) return;

    e.preventDefault();
    e.stopPropagation();

    const modal = reviewLink.closest(".ombraz-v2pdp__dialog-content");
    if (!modal) return;

    const productLink = modal.querySelector('a[href*="/products/"]');
    if (productLink) {
      window.location.href = `${productLink.href}#product-reviews`;
    }
  };

  // Attach the listener
  document.body.addEventListener("click", clickHandler, true);

  // Optional: Return cleanup function if needed
  return () => {
    document.body.removeEventListener("click", clickHandler, true);
  };
}

// Debounced version of the function
function debouncedUpdateMobileStarText() {
  let timeoutId;

  return function () {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      updateMobileStarText();
    }, 250); // 250ms delay
  };
}

/**
 * Sets up tooltip functionality for riser shipping information.
 * Creates an info icon with popup explaining shipping details.
 */
function setupShippingDelayTooltip() {
  const shippingDelayInfo = document.querySelector(".shipping-delay-message");

  if (!shippingDelayInfo) return;

  // Add event listeners for the info icon
  const infoIcon = shippingDelayInfo.querySelector(".info-icon");
  const infoPopup = shippingDelayInfo.querySelector(".info-popup");

  if (infoIcon && infoPopup) {
    const togglePopup = (e) => {
      e.stopPropagation();
      const isHidden = infoPopup.hidden;
      infoPopup.hidden = !isHidden;

      if (!isHidden) {
        document.removeEventListener("click", closePopup);
      } else {
        setTimeout(() => {
          document.addEventListener("click", closePopup);
        }, 0);
      }
    };

    const closePopup = () => {
      infoPopup.hidden = true;
      document.removeEventListener("click", closePopup);
    };

    infoIcon.addEventListener("click", togglePopup);
  }
}

/**
 * Handles click events on addon toggle items.
 * Navigates to the product URL associated with the clicked item.
 */
function addonItemClickHandler() {
  const addonItems = document.querySelectorAll(
    "[data-addon-toggle-product-url]"
  );

  if (!addonItems.length) return;

  addonItems.forEach((item) => {
    item.addEventListener("click", (e) => {
      // Ignore clicks on these elements
      const ignoreElements = [
        ".ombraz-v2pdp__dialog-opener",
        ".ombraz-v2pdp__toggle",
        ".ombraz-v2pdp__toggle-input",
        ".ombraz-v2pdp__dialog",
        ".ombraz-v2pdp__dialog-content",
        ".ombraz-v2pdp__dialog-close",
        ".ombraz-v2pdp__dialog-overlay",
      ];

      // Check if click target or its parents match any ignore elements
      const shouldIgnore = ignoreElements.some((selector) =>
        e.target.closest(selector)
      );

      if (!shouldIgnore) {
        e.preventDefault();
        const productUrl = item.getAttribute("data-addon-toggle-product-url");
        window.location.href = productUrl;
      }
    });
  });
}

/**
 * Handles image zoom for the feature swiper.
 * Cleans up existing zoom containers and adds new ones.
 * Handles both desktop hover and mobile touch interactions.
 */
function handleImageZoom(containerSelector = ".v2pdp-feature-swiper") {
  if (!document.querySelector("#zoom-container-styles")) {
    const style = document.createElement("style");
    style.id = "zoom-container-styles";
    style.textContent = `
      .zoom-container {
        overflow: hidden;
        position: relative;
      }
      .zoom-container img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transform: none !important;
        transition: transform 0.3s ease-out !important;
      }
      .zoom-container:hover img,
      .zoom-container img:hover {
        transform: none !important;
      }
      .zoom-container.active {
        overflow: visible;
      }
      .zoom-container.active img {
        position: relative;
        z-index: 1000;
        transform: scale(1.5) !important;
      }
    `;
    document.head.appendChild(style);
  }

  const container = document.querySelector(containerSelector);
  if (!container) return;

  const oldContainers = container.querySelectorAll(".zoom-container");
  oldContainers.forEach((container) => {
    const img = container.querySelector("img");
    if (img) container.replaceWith(img);
  });

  const slides = container.querySelectorAll(
    '.swiper-slide:not([style*="display: none"]), .prod-accs-swiper__slide:not([style*="display: none"])'
  );
  const isDesktop = window.matchMedia("(min-width: 1025px)").matches;

  slides.forEach((slide) => {
    if (
      slide.querySelector(".pdp-youtube-vdo") ||
      slide.querySelector(".youtube-player") ||
      slide.querySelector(".play-yt-button") ||
      slide.querySelector(".prod-accs__video") ||
      slide.classList.contains("video")
    )
      return;

    const img = slide.querySelector("img");
    if (!img) return;

    const zoomContainer = document.createElement("div");
    zoomContainer.className = "zoom-container";
    img.parentNode.insertBefore(zoomContainer, img);
    zoomContainer.appendChild(img);

    let isZoomed = false;

    if (isDesktop) {
      zoomContainer.addEventListener("dblclick", function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (!isZoomed) {
          zoomContainer.classList.add("active");
          isZoomed = true;

          const mouseMoveHandler = (e) => {
            if (!isZoomed) return;
            const rect = zoomContainer.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;
            requestAnimationFrame(() => {
              img.style.transformOrigin = `${x * 100}% ${y * 100}%`;
            });
          };

          zoomContainer.addEventListener("mousemove", mouseMoveHandler);
          zoomContainer._mouseMoveHandler = mouseMoveHandler;

          const escHandler = (e) => {
            if (e.key === "Escape") {
              cleanupZoom();
            }
          };
          document.addEventListener("keydown", escHandler);
          zoomContainer._escHandler = escHandler;
        } else {
          cleanupZoom();
        }
      });

      function cleanupZoom() {
        zoomContainer.classList.remove("active");
        isZoomed = false;
        if (zoomContainer._mouseMoveHandler) {
          zoomContainer.removeEventListener(
            "mousemove",
            zoomContainer._mouseMoveHandler
          );
        }
        if (zoomContainer._escHandler) {
          document.removeEventListener("keydown", zoomContainer._escHandler);
        }
      }
    }
  });
}

// Update the jQuery ready function
$(document).ready(function () {
  initKlaviyoReviews();
  handleModalReviewLinks();
  addonItemClickHandler();
  const debouncedUpdate = debouncedUpdateMobileStarText();

  $(window).on("resize", debouncedUpdate);

  handleActivityLPAdsControl();
  handleMenuOpeningStickyHeader();
  redirectCollectionAll();
  handleClickOnProductReviewLink();
  setupShippingDelayTooltip();
  setTimeout(handleStyleChangeKlaviyoReview, 2000);
  setTimeout(updateMobileStarText, 2000);
});
